import mockData from '../mock/mock_data.json';
import type { Story, StoryConfiguration, StoryContent, CreationTask, Comment } from '../types';

// 工具函数：模拟异步延迟
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const API = {
  // 故事相关
  stories: {
    // 获取故事列表
    list: async (params: {
      page: number;
      pageSize: number;
      category?: string;
      ageGroup?: string;
      status?: string;
      visibility?: 'public' | 'private';
    }) => {
      await delay(500); // 模拟网络延迟
      let stories = mockData.stories as Story[];
      
      // 筛选
      if (params.visibility) {
        stories = stories.filter(s => s.visibility === params.visibility);
      }
      if (params.status) {
        stories = stories.filter(s => s.status === params.status);
      }
      if (params.category) {
        stories = stories.filter(s => s.category.includes(params.category));
      }

      // 分页
      const start = (params.page - 1) * params.pageSize;
      const end = start + params.pageSize;
      
      return {
        items: stories.slice(start, end),
        total: stories.length
      };
    },

    // 获取故事详情
    get: async (id: string) => {
      await delay(300);
      const story = (mockData.stories as Story[]).find(s => s.id === id);
      const config = (mockData.configurations as StoryConfiguration[]).find(c => c.storyId === id);
      const contents = (mockData.contents as StoryContent[]).filter(c => c.storyId === id);
      
      if (!story) throw new Error('Story not found');
      
      return { story, config, contents };
    },

    // 创建新故事
    create: async (params: {
      title: string;
      description?: string;
      category?: string;
    }): Promise<Story> => {
      await delay(300);
      const newStory: Story = {
        id: `story_${Date.now()}`,
        authorId: 'usr_test0000000001', // 模拟当前用户
        title: params.title,
        description: params.description || '',
        coverImageUrl: '',
        category: params.category || '',
        status: 'draft',
        visibility: 'private',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      return newStory;
    },

    // 更新故事
    update: async (id: string, params: Partial<Story>): Promise<Story> => {
      await delay(300);
      const story = (mockData.stories as Story[]).find(s => s.id === id);
      if (!story) throw new Error('Story not found');
      
      const updatedStory = {
        ...story,
        ...params,
        updatedAt: new Date().toISOString()
      };

      if (params.status === 'published') {
        updatedStory.publishedAt = new Date().toISOString();
      }

      return updatedStory;
    },

    // 获取我的作品列表
    getMyWorks: async () => {
      // 模拟当前用户 ID
      const currentUserId = 'usr_test0000000001'
      
      // 从 mock 数据中过滤出当前用户的故事
      const myStories = mockData.stories.filter(story => 
        story.authorId === currentUserId
      )

      // 为每个故事添加作者信息
      const storiesWithAuthor = await Promise.all(myStories.map(async story => {
        const author = mockData.users.find(user => user.id === story.authorId)
        return {
          ...story,
          author
        }
      }))

      return storiesWithAuthor
    },

    // 删除故事
    delete: async (id: string) => {
      // 模拟删除操作
      const index = mockData.stories.findIndex(story => story.id === id)
      if (index === -1) {
        throw new Error('Story not found')
      }
      
      // 实际项目中这里会调用后端 API
      console.log('删除故事:', id)
      
      return true
    }
  },

  // 创作相关
  creation: {
    // 开始创作任务
    startCreation: async (params: {
      storyId: string;
      prompt: string;
      config: Partial<StoryConfiguration>;
    }): Promise<CreationTask> => {
      await delay(300);
      return {
        id: `task_${Date.now()}`,
        storyId: params.storyId,
        userId: 'usr_test0000000001',
        taskType: 'STORY_GENERATION',
        status: 'PROCESSING',
        progress: 0,
        startedAt: new Date().toISOString()
      };
    },

    // 获取创作任务状态
    getTaskStatus: async (taskId: string): Promise<CreationTask> => {
      await delay(200);
      // 模拟任务进度
      const progress = Math.min(100, Math.floor(Math.random() * 100));
      return {
        id: taskId,
        storyId: 'story_test',
        userId: 'usr_test0000000001',
        taskType: 'STORY_GENERATION',
        status: progress === 100 ? 'COMPLETED' : 'PROCESSING',
        progress,
        startedAt: new Date().toISOString()
      };
    },

    // 保存故事内容
    saveContent: async (params: {
      storyId: string;
      pageNumber: number;
      textContent: string;
      imageUrl: string;
      audioUrl?: string;
    }): Promise<StoryContent> => {
      await delay(300);
      return {
        id: `content_${Date.now()}`,
        ...params
      };
    }
  },

  // 社交互动
  social: {
    // 获取评论列表
    getComments: async (params: {
      storyId: string;
      page: number;
      pageSize: number;
    }) => {
      await delay(300);
      const comments = (mockData.comments as Comment[])
        .filter(c => c.storyId === params.storyId);
      
      const start = (params.page - 1) * params.pageSize;
      const end = start + params.pageSize;
      
      return {
        items: comments.slice(start, end),
        total: comments.length
      };
    }
  }
}; 